import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '../../components/Seo';
import ITTrainingHeader from '../../components/ITTrainingHeader';
import ITTrainingFooter from '../../components/ITTrainingFooter';

const specialPrograms = [
  {
    id: 'free',
    slug: 'free-training',
    icon: '🎁',
    badge: 'FREE',
    badgeColor: '#27ae60',
    title: 'Free Training Program',
    tagline: 'Start learning without spending a rupee',
    desc: 'We believe financial constraints should never stop talented individuals from building a career in IT. Our free training program gives you a solid foundation in a core technology so you can evaluate your interest before committing to a full course.',
    features: [
      'Introductory sessions for selected technologies',
      'Access to study materials and demo projects',
      'Guidance from experienced trainers',
      'Certificate of participation',
      'No registration fee required',
    ],
    cta: 'Register for Free',
  },
  {
    id: 'weekend',
    slug: 'weekend-training',
    icon: '📅',
    badge: 'WEEKEND',
    badgeColor: '#e67e22',
    title: 'Weekend Training Program',
    tagline: 'Learn while you work — Saturdays & Sundays only',
    desc: 'Designed for working professionals and college students who cannot attend weekday batches. Get the same quality training condensed into weekend sessions so your week stays uninterrupted.',
    features: [
      'PHP Weekend Training',
      '.Net Weekend Training',
      'Java Weekend Training',
      'Android Weekend Training',
      'Flexible batch timings (morning & evening)',
      'Same curriculum as regular batches',
      'Dedicated weekend faculty',
    ],
    cta: 'Book a Weekend Batch',
  },
  {
    id: 'corporate',
    slug: 'corporate-training',
    icon: '🏢',
    badge: 'CORPORATE',
    badgeColor: '#2980b9',
    title: 'Corporate Training Program',
    tagline: 'Upskill your entire team with customised training',
    desc: 'We partner with companies to deliver tailored IT training for their teams. Whether you need to onboard new hires, upskill an existing workforce, or train on a specific technology stack — we build a custom program around your goals.',
    features: [
      'Customised curriculum aligned to your tech stack',
      'On-site or online delivery options',
      'Group discounts for 5+ employees',
      'Progress tracking and assessment reports',
      'Post-training support and Q&A sessions',
      'Certificate of completion for all participants',
    ],
    cta: 'Request Corporate Proposal',
  },
];

function SpecialTraining() {
  return (
    <div className="itp-shell">
      <Seo
        title="Special Training Programs | Free, Weekend, Corporate IT Courses | Vedanskrix"
        description="Explore special IT training programs at Vedanskrix. Free training, weekend batches, and corporate training for students and working professionals."
        keywords={['free IT training','weekend IT course','corporate IT training','free programming course','weekend batch','employee training','IT upskilling','corporate workshops Gujarat']}
      />
      <ITTrainingHeader />

      {/* Hero */}
      <section className="itpage-hero itpage-hero--special">
        <div className="itpage-hero__overlay" />
        <div className="itp-container itpage-hero__content">
          <p className="itpage-hero__eyebrow">Vedanskrix IT Training</p>
          <h1 className="itpage-hero__title">Special Training Programs</h1>
          <p className="itpage-hero__sub">Free, Weekend &amp; Corporate options — training that fits your life</p>
          <div className="itpage-hero__crumb">
            <Link to="/">Home</Link>
            <span> › </span>
            <span>Special Training</span>
          </div>
        </div>
      </section>

      {/* Programs */}
      <section className="itpage-section">
        <div className="itp-container">
          <div className="itpage-section-head">
            <span className="itpage-section-tag">3 Special Programs</span>
            <h2 className="itpage-section-title">Training Designed Around You</h2>
            <p className="itpage-section-sub">
              No matter your schedule or budget, we have a program to keep your IT career moving forward.
            </p>
          </div>

          <div className="itpage-special-grid">
            {specialPrograms.map((prog) => (
              <div key={prog.id} className="itpage-special-card">
                <div className="itpage-special-card__top" style={{'--sp-color': prog.badgeColor}}>
                  <span className="itpage-special-card__icon">{prog.icon}</span>
                  <span className="itpage-special-card__badge" style={{background: prog.badgeColor}}>{prog.badge}</span>
                </div>
                <div className="itpage-special-card__body">
                  <h3 className="itpage-special-card__title">{prog.title}</h3>
                  <p className="itpage-special-card__tagline">{prog.tagline}</p>
                  <p className="itpage-special-card__desc">{prog.desc}</p>
                  <ul className="itpage-special-card__list">
                    {prog.features.map(f => (
                      <li key={f}><span className="itpage-check">✔</span> {f}</li>
                    ))}
                  </ul>
                  <div className="itpage-course-card__actions" style={{marginTop: '16px', display: 'flex', gap: '10px', flexWrap: 'wrap'}}>
                    <Link
                      to={`/course/${prog.slug}`}
                      className="itpage-special-card__btn"
                      style={{background: prog.badgeColor}}
                    >
                      View Details →
                    </Link>
                    <Link
                      to="/contact"
                      className="itpage-course-card__btn itpage-course-card__btn--outline"
                      style={{border: `2px solid ${prog.badgeColor}`, color: prog.badgeColor, padding: '9px 18px'}}
                    >
                      {prog.cta}
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Special */}
      <section className="itpage-section itpage-section--alt">
        <div className="itp-container">
          <div className="itpage-section-head">
            <h2 className="itpage-section-title">Why Choose a Special Program?</h2>
          </div>
          <div className="itpage-why-grid">
            {[
              { icon: '⏰', title: 'Flexible Scheduling', desc: 'Choose the timing that fits your life — weekdays, weekends, or custom batches.' },
              { icon: '💰', title: 'Budget-Friendly', desc: 'From completely free to affordable group rates — quality training at every price point.' },
              { icon: '🏆', title: 'Same Quality', desc: 'Special programs use the same curriculum, trainers, and projects as our regular batches.' },
              { icon: '🤝', title: 'Placement Support', desc: 'All special program graduates get full access to our placement assistance services.' },
            ].map(w => (
              <div key={w.title} className="itp-why-card">
                <span className="itp-why-card__icon">{w.icon}</span>
                <h4 className="itp-why-card__title">{w.title}</h4>
                <p className="itp-why-card__text">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="itpage-cta-strip">
        <div className="itp-container itpage-cta-strip__inner">
          <div>
            <h3 className="itpage-cta-strip__title">Interested in a Special Program?</h3>
            <p className="itpage-cta-strip__sub">Contact us today and we'll find the best training option for your situation.</p>
          </div>
          <Link to="/contact" className="itpage-cta-strip__btn">Contact Us →</Link>
        </div>
      </section>

      <ITTrainingFooter />
    </div>
  );
}

export default SpecialTraining;
